import { memo, useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';

interface MermaidDiagramProps {
  chart: string;
  id?: string;
}

// Global flag to ensure mermaid is initialized only once
let mermaidInitialized = false;

const MermaidDiagram = memo(({ chart, id }: MermaidDiagramProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isValid, setIsValid] = useState(true);
  const [diagramId] = useState(() => id || `mermaid-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`);

  useEffect(() => {
    // Initialize Mermaid with configuration only once globally
    if (!mermaidInitialized) {
      mermaid.initialize({
        startOnLoad: false,
        theme: 'default',
        securityLevel: 'loose',
        fontFamily: 'inherit',
        suppressErrorRendering: false
      });
      mermaidInitialized = true;
    }
  }, []);

  useEffect(() => {
    if (!chart || !chart.trim() || !ref.current) return;

    const renderDiagram = async () => {
      try {
        // Clear previous content first
        if (ref.current) {
          ref.current.innerHTML = '';
        }

        // Generate a truly unique ID for this render
        const uniqueId = `${diagramId}-${Date.now()}`;
        
        // Validate the Mermaid syntax first
        await mermaid.parse(chart);
        setIsValid(true);

        // Render the diagram with unique ID
        const { svg } = await mermaid.render(uniqueId, chart);
        
        if (ref.current) {
          ref.current.innerHTML = svg;
        }
      } catch (error) {
        // Log invalid syntax to console for debugging (as requested)
        console.error('Invalid Mermaid syntax:', error);
        setIsValid(false);
        if (ref.current) {
          ref.current.innerHTML = '';
        }
      }
    };

    // Add a small delay to handle streaming tokens more gracefully
    const timeoutId = setTimeout(renderDiagram, 150);
    
    return () => clearTimeout(timeoutId);
  }, [chart, diagramId]);

  // Don't render anything if the syntax is invalid
  if (!isValid || !chart?.trim()) {
    return null;
  }

  return (
    <div className="mermaid-diagram my-4 p-4 border border-gray-200 rounded-lg bg-white overflow-auto">
      <div ref={ref} className="flex justify-center" />
    </div>
  );
});

MermaidDiagram.displayName = 'MermaidDiagram';

export default MermaidDiagram;
